
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FileText, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import PositPrivacyDropdown from "./PositPrivacyDropdown";
import EditPositSettingsDialog from "./EditPositSettingsDialog";
import { Posit } from "@/types/posit";

interface PositHeaderProps {
  posit: Posit | null;
  isPremium: boolean;
  onUpdatePrivacy: (privacy: 'public' | 'private' | 'exclusive') => void;
  className?: string;
}

const PositHeader = ({ posit, isPremium, onUpdatePrivacy, className = "" }: PositHeaderProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(posit?.title || '');
  const { toast } = useToast();
  const { positId } = useParams();

  const handleUpdateTitle = async () => {
    if (!posit?.id || !newTitle.trim()) return;

    try {
      const { error } = await supabase
        .from('posits')
        .update({ 
          title: newTitle.trim(),
          updated_by: 'user'
        })
        .eq('id', posit.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Posit title updated successfully",
      });
      
      setIsEditing(false);
      window.location.reload();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update the title. Please try again.",
      });
    }
  };

  if (!posit) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-4">Create Your First Posit</h2>
        <p className="text-muted-foreground">
          Start by adding your thoughts and let POSIT help you organize them.
        </p>
      </div>
    );
  }

  const handleSettingsUpdated = () => {
    window.location.reload();
  };

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            {posit.title}
          </h1>
          <Dialog open={isEditing} onOpenChange={setIsEditing}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Posit Title</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Enter new title"
                  className="w-full"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateTitle}>
                    Save
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-row items-center gap-2">
          <Button asChild variant="outline" size="default">
            <Link to={`/posit-details/${positId}`}>
              <FileText className="h-4 w-4 mr-2" />
              Details
            </Link>
          </Button>

          <EditPositSettingsDialog
            positId={posit.id}
            currentTitle={posit.title}
            currentPublicDescription={(posit as any).public_description}
            currentPrivacy={posit.privacy}
            currentIsMonetized={(posit as any).is_monetized || false}
            currentPrice={(posit as any).subscription_price || null}
            currentResponseType={(posit as any).response_type || "you"}
            onSettingsUpdated={handleSettingsUpdated}
          />

          <PositPrivacyDropdown
            privacy={posit.privacy as 'public' | 'private' | 'exclusive'}
            onUpdatePrivacy={onUpdatePrivacy}
            isPremium={isPremium}
          />
        </div>

        <h2 className="text-2xl font-bold tracking-tight">Description</h2>
      </div>
    </div>
  );
};

export default PositHeader;
